import React, { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin } from 'lucide-react';
import {
    computeCorridorDisplayLines,
    getUsablePolygons,
    moduleFitsUsableArea,
    campusRigidFits,
    pointerToModuleAngle,
    normalizeAngle360,
    snapAngleWithStillThreshold,
    snapAngleToEdges,
    getSnapEdgeAngles,
    getUsableEdgeLabels,
    getOrientedBoxCorners,
    hitTestSubAreas,
    hitTestCutLines,
    hitTestPlacedModules,
    hitTestHatchModules,
    hitTestResultM1Modules,
    hitTestM2Modules,
    hitTestGroupOutlines,
    formatHatchModuleId,
    formatResultM1ModuleId,
    formatResultM2ModuleId,
    formatGroupSelectionId,
    groupClusterCentroid,
    getPlacedModuleDimensions,
    updateAreaSelection,
    SUB_AREA_COLORS,
    SUB_AREA_STROKES,
    getManualHatchModules,
    formatAreaLabel,
    getPolygonCentroid,
    getHatchStepAfterOrigin,
    isDivisionBorderPoint,
    pointsNearEachOther,
    findBoundaryEdgeInsertion,
} from './sitePlannerGeometry';
import { PenTool } from 'lucide-react';
import { findSnapOnPolygons } from '../../utils/snapping';

export function latLngToMeters(lat, lng, origin) {
    if (!origin) return [0, 0];
    const latRad = (origin.lat * Math.PI) / 180.0;
    const y = (lat - origin.lat) * 111132.92;
    const x = (lng - origin.lng) * 111319.9 * Math.cos(latRad);
    return [x, y];
}

export function metersToLatLng(x, y, origin) {
    if (!origin) return { lat: 0, lng: 0 };
    const latRad = (origin.lat * Math.PI) / 180.0;
    const lat = origin.lat + y / 111132.92;
    const lng = origin.lng + x / (111319.9 * Math.cos(latRad));
    return { lat, lng };
}

export default function MapViewport({
    boundary,
    setBoundary,
    setback,
    results,
    m1Params,
    m2Params,
    m3Params,
    m4Params,
    module3Positions = [],
    module4Positions = [],
    origin,
    setOrigin,
    beginUndoableEdit,
    onM2Commit,
    onM3Commit,
    onM4Commit,
    onInfraPlace,
    manualMode,
    toolMode,
    cutLines,
    corridorWallLines,
    subAreas,
    hasSplitSubAreas = false,
    selectedAreaIds,
    setSelectedAreaIds,
    areaHatchConfigs,
    updateAreaHatchConfig,
    hatchAngleStep,
    setHatchAngleStep,
    setToolMode,
    setError,
    areaLayouts,
    placedModules,
    selectedCutLineId,
    setSelectedCutLineId,
    selectedModuleId,
    setSelectedModuleId,
    onCommitDivisionPolyline,
    onCommitCorridor,
    corridorWidth,
    onManualModeChange,
    paletteDragType,
    setPaletteDragType,
    onPlaceModule,
    onUpdatePlacedModule,
    onUpdateHatchModule,
    onUpdateResultM1,
    onUpdateResultM2,
    onTranslateGroup,
    onTranslateCluster,
    onRefitCampus,
}) {
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const originRef = useRef(origin);
    const manualModeRef = useRef(manualMode);
    const toolModeRef = useRef(toolMode);
    const hatchAngleStepRef = useRef(hatchAngleStep);
    const areaHatchConfigsRef = useRef(areaHatchConfigs);
    const selectedAreaIdsRef = useRef(selectedAreaIds);
    const updateAreaHatchConfigRef = useRef(updateAreaHatchConfig);
    const setHatchAngleStepRefFn = useRef(setHatchAngleStep);
    const setToolModeRef = useRef(setToolMode);
    const setErrorRef = useRef(setError);
    const snapEdgeAnglesRef = useRef([]);
    const subAreasRef = useRef(subAreas);
    const cutLinesRef = useRef(cutLines);
    const placedModulesRef = useRef(placedModules);
    const areaLayoutsRef = useRef(areaLayouts);
    const resultsRef = useRef(results);
    const onUpdatePlacedModuleRef = useRef(onUpdatePlacedModule);
    const onUpdateHatchModuleRef = useRef(onUpdateHatchModule);
    const onUpdateResultM1Ref = useRef(onUpdateResultM1);
    const onUpdateResultM2Ref = useRef(onUpdateResultM2);
    const onTranslateGroupRef = useRef(onTranslateGroup);
    const onTranslateClusterRef = useRef(onTranslateCluster);
    const onRefitCampusRef = useRef(onRefitCampus);
    const m1ParamsRef = useRef(m1Params);
    const m2ParamsRef = useRef(m2Params);
    const splitDraftRef = useRef([]);
    const onCommitDivisionPolylineRef = useRef(onCommitDivisionPolyline);
    const corridorDraftRef = useRef([]);
    const onCommitCorridorRef = useRef(onCommitCorridor);
    const corridorWidthRef = useRef(corridorWidth);
    const getMapSnapPointRef = useRef((meters) => meters);
    const borderPolygonsRef = useRef([]);
    const rotationInputRef = useRef(null);
    const pointerStillSinceRef = useRef(Date.now());
    const lastPointerRef = useRef(null);
    const layersRef = useRef({
        markers: [],
        boundaryPolyline: null,
        boundaryPolygon: null,
        usablePolygons: [],
        subAreaPolygons: [],
        cutLines: [],
        hatchOverlay: [],
        modules: [],
        edgeLabels: [],
        tileLayer: null,
    });

    const [tileType, setTileType] = useState('dark');
    const [contextMenu, setContextMenu] = useState(null);
    const [rotation, setRotation] = useState(null);
    const [draftM2List, setDraftM2List] = useState(null);
    const [hatchAnglePreview, setHatchAnglePreview] = useState(null);
    const [corridorDraft, setCorridorDraft] = useState([]);
    const [corridorCursor, setCorridorCursor] = useState(null);
    const [splitDraftPoints, setSplitDraftPoints] = useState([]);
    const [splitCursor, setSplitCursor] = useState(null);

    const activeHatchAreaId =
        selectedAreaIds?.length === 1 ? selectedAreaIds[0] : null;
    const activeHatch = activeHatchAreaId ? areaHatchConfigs?.[activeHatchAreaId] : null;

    const m2Len = m2Params.length || 16.0;
    const m2Wid = m2Params.width || 2.6;
    const m3Len = m3Params?.length || 8.0;
    const m3Wid = m3Params?.width || 3.0;
    const m4Len = m4Params?.length || 6.0;
    const m4Wid = m4Params?.width || 2.5;
    const usablePolygons = getUsablePolygons(results);
    const displayM2 = draftM2List ?? results?.module2;
    const manualModules = getManualHatchModules(areaLayouts);
    const snapEdgeAngles = getSnapEdgeAngles(boundary, subAreas, results);
    const corridorBorderPolygons = [
        ...(boundary.length >= 3 ? [boundary] : []),
        ...usablePolygons,
        ...(subAreas || []).map((a) => a.boundary).filter(Boolean),
    ];

    useEffect(() => {
        onCommitCorridorRef.current = onCommitCorridor;
        onCommitDivisionPolylineRef.current = onCommitDivisionPolyline;
        corridorWidthRef.current = corridorWidth;
        corridorDraftRef.current = corridorDraft;
        splitDraftRef.current = splitDraftPoints;
        const polygons = [
            ...(boundary.length >= 3 ? [boundary] : []),
            ...getUsablePolygons(results),
            ...(subAreas || []).map((a) => a.boundary).filter(Boolean),
        ];
        getMapSnapPointRef.current = (meters) => {
            const snap = findSnapOnPolygons(meters, polygons, 1.0);
            return snap?.point || meters;
        };
        borderPolygonsRef.current = polygons;
    }, [onCommitCorridor, onCommitDivisionPolyline, corridorWidth, corridorDraft, splitDraftPoints, boundary, results, subAreas]);

    useEffect(() => {
        subAreasRef.current = subAreas;
        cutLinesRef.current = cutLines;
        placedModulesRef.current = placedModules;
        areaLayoutsRef.current = areaLayouts;
        resultsRef.current = results;
        onUpdatePlacedModuleRef.current = onUpdatePlacedModule;
        onUpdateHatchModuleRef.current = onUpdateHatchModule;
        onUpdateResultM1Ref.current = onUpdateResultM1;
        onUpdateResultM2Ref.current = onUpdateResultM2;
        onTranslateGroupRef.current = onTranslateGroup;
        onTranslateClusterRef.current = onTranslateCluster;
        onRefitCampusRef.current = onRefitCampus;
        m1ParamsRef.current = m1Params;
        m2ParamsRef.current = m2Params;
        originRef.current = origin;
        manualModeRef.current = manualMode;
        toolModeRef.current = toolMode;
        hatchAngleStepRef.current = hatchAngleStep;
        areaHatchConfigsRef.current = areaHatchConfigs;
        selectedAreaIdsRef.current = selectedAreaIds;
        updateAreaHatchConfigRef.current = updateAreaHatchConfig;
        setHatchAngleStepRefFn.current = setHatchAngleStep;
        setToolModeRef.current = setToolMode;
        setErrorRef.current = setError;
        snapEdgeAnglesRef.current = snapEdgeAngles;
    }, [origin, manualMode, toolMode, hatchAngleStep, areaHatchConfigs, selectedAreaIds, subAreas, cutLines, placedModules, areaLayouts, results, onUpdatePlacedModule, onUpdateHatchModule, onUpdateResultM1, onUpdateResultM2, onTranslateGroup, onTranslateCluster, onRefitCampus, m1Params, m2Params, updateAreaHatchConfig, setHatchAngleStep, setToolMode, setError, snapEdgeAngles]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (corridorDraftRef.current.length > 0) {
                    corridorDraftRef.current = [];
                    setCorridorDraft([]);
                    setCorridorCursor(null);
                }
                if (splitDraftRef.current.length > 0) {
                    splitDraftRef.current = [];
                    setSplitDraftPoints([]);
                    setSplitCursor(null);
                }
            }
            if (
                e.key === 'Enter'
                && toolModeRef.current === 'corridor'
                && corridorDraftRef.current.length >= 2
            ) {
                e.preventDefault();
                onCommitCorridorRef.current(corridorDraftRef.current);
                corridorDraftRef.current = [];
                setCorridorDraft([]);
                setCorridorCursor(null);
            }
            if (
                e.key === 'Enter'
                && toolModeRef.current === 'split'
                && splitDraftRef.current.length >= 2
            ) {
                const last = splitDraftRef.current[splitDraftRef.current.length - 1];
                if (isDivisionBorderPoint(last, null, borderPolygonsRef.current, 1.0)) {
                    e.preventDefault();
                    onCommitDivisionPolylineRef.current(splitDraftRef.current, false);
                    splitDraftRef.current = [];
                    setSplitDraftPoints([]);
                    setSplitCursor(null);
                }
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        if (toolMode !== 'corridor') {
            corridorDraftRef.current = [];
            setCorridorDraft([]);
            setCorridorCursor(null);
        }
        if (toolMode !== 'split') {
            splitDraftRef.current = [];
            setSplitDraftPoints([]);
            setSplitCursor(null);
        }
    }, [toolMode]);

    useEffect(() => {
        if (!manualMode || toolMode !== 'hatchOrigin' || hatchAngleStep !== 'direction') {
            setHatchAnglePreview(null);
            return undefined;
        }
        const onKeyDown = (e) => {
            if (e.key !== 'Tab') return;
            const areaId =
                selectedAreaIdsRef.current?.length === 1
                    ? selectedAreaIdsRef.current[0]
                    : null;
            if (!areaId) return;
            const cfg = areaHatchConfigsRef.current?.[areaId];
            if (cfg?.angle == null) return;
            e.preventDefault();
            setHatchAngleStepRefFn.current(null);
            setToolModeRef.current('select');
            setHatchAnglePreview(null);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [manualMode, toolMode, hatchAngleStep]);

    useEffect(() => {
        if (
            !mapRef.current
            || !manualMode
            || toolMode !== 'hatchOrigin'
            || hatchAngleStep !== 'direction'
        ) {
            return undefined;
        }
        const map = mapRef.current;
        const onMouseMove = (e) => {
            const areaId =
                selectedAreaIdsRef.current?.length === 1
                    ? selectedAreaIdsRef.current[0]
                    : null;
            if (!areaId) return;
            const cfg = areaHatchConfigsRef.current?.[areaId];
            if (!cfg?.angleRef) return;
            const meters = latLngToMeters(e.latlng.lat, e.latlng.lng, originRef.current);
            const rawAngle = pointerToModuleAngle(
                meters[0],
                meters[1],
                cfg.angleRef[0],
                cfg.angleRef[1],
            );
            const snapped = snapAngleToEdges(rawAngle, snapEdgeAnglesRef.current, 2);
            setHatchAnglePreview(normalizeAngle360(snapped));
        };
        map.on('mousemove', onMouseMove);
        return () => {
            map.off('mousemove', onMouseMove);
        };
    }, [manualMode, toolMode, hatchAngleStep]);

    useEffect(() => {
        if (
            !mapRef.current
            || !manualMode
            || toolMode !== 'corridor'
        ) {
            return undefined;
        }
        const map = mapRef.current;
        const onMouseMove = (e) => {
            const meters = latLngToMeters(e.latlng.lat, e.latlng.lng, originRef.current);
            const snapped = getMapSnapPointRef.current(meters);
            setCorridorCursor(snapped);
        };
        map.on('mousemove', onMouseMove);
        return () => {
            map.off('mousemove', onMouseMove);
        };
    }, [manualMode, toolMode]);

    useEffect(() => {
        if (!mapRef.current || !manualMode || toolMode !== 'split') {
            return undefined;
        }
        const map = mapRef.current;
        const onMouseMove = (e) => {
            const meters = latLngToMeters(e.latlng.lat, e.latlng.lng, originRef.current);
            const snapped = getMapSnapPointRef.current(meters);
            setSplitCursor(snapped);
        };
        map.on('mousemove', onMouseMove);
        return () => {
            map.off('mousemove', onMouseMove);
        };
    }, [manualMode, toolMode]);

    const getOrientedBoxLatLngs = useCallback(
        (cx, cy, length, width, angleDeg, orig) => {
            const corners = getOrientedBoxCorners(cx, cy, length, width, angleDeg);
            return corners.map(([mx, my]) => {
                const coord = metersToLatLng(mx, my, orig);
                return [coord.lat, coord.lng];
            });
        },
        [],
    );

    const attachSelectDragHandle = useCallback(
        (map, metersX, metersY, length, width, angle, color, onUpdate, layers, linkedPoly) => {
            if (!manualMode || toolMode !== 'select' || !origin) return;
            const centerCoord = metersToLatLng(metersX, metersY, origin);
            const dragIcon = L.divIcon({
                className: 'custom-module-drag-handle',
                html: `<div style="width: 10px; height: 10px; background: ${color}; border: 1.5px solid white; border-radius: 50%; cursor: move;"></div>`,
                iconSize: [10, 10],
                iconAnchor: [5, 5],
            });
            let lastValidLatLng = { lat: centerCoord.lat, lng: centerCoord.lng };
            let pending = null;
            const dragMarker = L.marker([centerCoord.lat, centerCoord.lng], {
                icon: dragIcon,
                draggable: true,
                zIndexOffset: 500,
            }).addTo(map);

            dragMarker.on('dragstart', () => beginUndoableEdit());
            dragMarker.on('drag', (e) => {
                const newLatLng = e.target.getLatLng();
                const newMeters = latLngToMeters(newLatLng.lat, newLatLng.lng, origin);
                if (
                    moduleFitsUsableArea(
                        newMeters[0],
                        newMeters[1],
                        length,
                        width,
                        angle,
                        usablePolygons,
                    )
                ) {
                    pending = { x: newMeters[0], y: newMeters[1] };
                    lastValidLatLng = newLatLng;
                    if (linkedPoly) {
                        linkedPoly.setLatLngs(
                            getOrientedBoxLatLngs(
                                newMeters[0],
                                newMeters[1],
                                length,
                                width,
                                angle,
                                origin,
                            ),
                        );
                    }
                } else {
                    e.target.setLatLng(lastValidLatLng);
                }
            });
            dragMarker.on('dragend', () => {
                if (pending) onUpdate(pending.x, pending.y);
            });
            layers.modules.push(dragMarker);
            return dragMarker;
        },
        [manualMode, toolMode, origin, usablePolygons, beginUndoableEdit, getOrientedBoxLatLngs],
    );

    useEffect(() => {
        if (mapRef.current) return undefined;

        const defaultCenter = [59.437, 24.7535];
        const map = L.map(mapContainerRef.current, {
            center: defaultCenter,
            zoom: 13,
            zoomControl: false,
            attributionControl: false,
        });

        L.control.zoom({ position: 'topright' }).addTo(map);
        mapRef.current = map;

        setTimeout(() => map.invalidateSize(), 100);

        map.on('click', (e) => {
            if (rotation) return;
            const { lat, lng } = e.latlng;
            const meters = latLngToMeters(lat, lng, originRef.current);

            if (manualModeRef.current) {
                const mode = toolModeRef.current;
                if (mode === 'split') {
                    const snap = findSnapOnPolygons(meters, borderPolygonsRef.current, 1.0);
                    const clickPt = snap?.point || meters;
                    const clickDetail = e.originalEvent?.detail || 1;
                    const draft = splitDraftRef.current;

                    if (
                        draft.length >= 3
                        && pointsNearEachOther(draft[0], clickPt, 1.0)
                    ) {
                        onCommitDivisionPolylineRef.current(draft, true);
                        splitDraftRef.current = [];
                        setSplitDraftPoints([]);
                        setSplitCursor(null);
                        return;
                    }

                    if (draft.length >= 1 && isDivisionBorderPoint(clickPt, snap, borderPolygonsRef.current, 1.0)) {
                        const last = draft[draft.length - 1];
                        const finished = pointsNearEachOther(last, clickPt, 0.05)
                            ? draft
                            : [...draft, clickPt];
                        if (finished.length >= 2) {
                            onCommitDivisionPolylineRef.current(finished, false);
                        }
                        splitDraftRef.current = [];
                        setSplitDraftPoints([]);
                        setSplitCursor(null);
                        return;
                    }

                    if (clickDetail >= 2) {
                        if (draft.length >= 2) {
                            const last = draft[draft.length - 1];
                            if (isDivisionBorderPoint(last, snap, borderPolygonsRef.current, 1.0)) {
                                onCommitDivisionPolylineRef.current(draft, false);
                                splitDraftRef.current = [];
                                setSplitDraftPoints([]);
                                setSplitCursor(null);
                            }
                        }
                        return;
                    }

                    const next = [...draft, clickPt];
                    splitDraftRef.current = next;
                    setSplitDraftPoints(next);
                    return;
                }
                if (mode === 'corridor') {
                    const snapped = getMapSnapPointRef.current(meters);
                    const clickDetail = e.originalEvent?.detail || 1;
                    if (clickDetail >= 2) {
                        if (corridorDraftRef.current.length >= 2) {
                            onCommitCorridorRef.current(corridorDraftRef.current);
                            corridorDraftRef.current = [];
                            setCorridorDraft([]);
                            setCorridorCursor(null);
                        }
                        return;
                    }
                    const next = [...corridorDraftRef.current, snapped];
                    corridorDraftRef.current = next;
                    setCorridorDraft(next);
                    return;
                }
                if (mode === 'select') {
                    const placedId = hitTestPlacedModules(
                        meters,
                        placedModulesRef.current,
                        m1ParamsRef.current,
                        m2ParamsRef.current,
                    );
                    if (placedId) {
                        setSelectedModuleId(placedId);
                        setSelectedCutLineId(null);
                        if (!e.originalEvent?.shiftKey) setSelectedAreaIds([]);
                        return;
                    }
                    const hatchHit = hitTestHatchModules(meters, areaLayoutsRef.current, m1ParamsRef.current);
                    if (hatchHit) {
                        setSelectedModuleId(formatHatchModuleId(hatchHit.areaId, hatchHit.index));
                        setSelectedCutLineId(null);
                        setSelectedAreaIds([]);
                        return;
                    }
                    const resultM1Idx = hitTestResultM1Modules(
                        meters,
                        resultsRef.current,
                        m1ParamsRef.current,
                    );
                    if (resultM1Idx !== -1) {
                        setSelectedModuleId(formatResultM1ModuleId(resultM1Idx));
                        setSelectedCutLineId(null);
                        setSelectedAreaIds([]);
                        return;
                    }
                    const m2HitLen = m2ParamsRef.current?.length || 18;
                    const m2HitWid = m2ParamsRef.current?.width || 4;
                    const resultM2Idx = hitTestM2Modules(
                        meters,
                        resultsRef.current?.module2,
                        m2HitLen,
                        m2HitWid,
                    );
                    if (resultM2Idx !== -1) {
                        setSelectedModuleId(formatResultM2ModuleId(resultM2Idx));
                        setSelectedCutLineId(null);
                        setSelectedAreaIds([]);
                        return;
                    }
                    const groupIdx = hitTestGroupOutlines(meters, resultsRef.current?.groups);
                    if (groupIdx !== -1) {
                        setSelectedModuleId(formatGroupSelectionId(groupIdx));
                        setSelectedCutLineId(null);
                        setSelectedAreaIds([]);
                        return;
                    }
                    const cutId = hitTestCutLines(meters, cutLinesRef.current, 1.0);
                    if (cutId) {
                        setSelectedCutLineId(cutId);
                        setSelectedModuleId(null);
                        setSelectedAreaIds([]);
                        return;
                    }
                    const areaId = hitTestSubAreas(meters, subAreasRef.current);
                    if (areaId) {
                        setSelectedCutLineId(null);
                        setSelectedModuleId(null);
                        setSelectedAreaIds((prev) =>
                            updateAreaSelection(prev, areaId, e.originalEvent?.shiftKey),
                        );
                    }
                    return;
                }
                if (mode === 'hatchOrigin') {
                    const areaId =
                        selectedAreaIdsRef.current?.length === 1
                            ? selectedAreaIdsRef.current[0]
                            : null;
                    if (!areaId) {
                        setErrorRef.current?.('Select exactly one area before setting hatch origin.');
                        return;
                    }
                    const step = hatchAngleStepRef.current;
                    const configs = areaHatchConfigsRef.current || {};
                    const active = configs[areaId] || {};
                    if (!step) {
                        updateAreaHatchConfigRef.current(areaId, { origin: meters });
                        const { nextStep, patch } = getHatchStepAfterOrigin(active, meters);
                        if (Object.keys(patch).length > 0) {
                            updateAreaHatchConfigRef.current(areaId, patch);
                        }
                        setHatchAngleStepRefFn.current(nextStep);
                        return;
                    }
                    if (step === 'reference') {
                        updateAreaHatchConfigRef.current(areaId, { angleRef: meters });
                        setHatchAngleStepRefFn.current('direction');
                        return;
                    }
                    if (step === 'direction' && active.angleRef) {
                        const angle = pointerToModuleAngle(
                            meters[0],
                            meters[1],
                            active.angleRef[0],
                            active.angleRef[1],
                        );
                        const snapped = snapAngleToEdges(
                            angle,
                            snapEdgeAnglesRef.current,
                            2,
                        );
                        updateAreaHatchConfigRef.current(areaId, {
                            angle: normalizeAngle360(snapped),
                            angleRef: active.angleRef,
                        });
                        setHatchAngleStepRefFn.current(null);
                        setToolModeRef.current('select');
                        setHatchAnglePreview(null);
                    }
                    return;
                }
                return;
            }

            setBoundary((prev) => {
                if (prev.length === 0) {
                    setOrigin({ lat, lng });
                    return [[0, 0]];
                }
                beginUndoableEdit();
                const snapPolys = prev.length >= 2 ? [prev] : [];
                const snap = findSnapOnPolygons(meters, snapPolys, 1.0);
                const clickPt = snap?.point || meters;
                const edgeInsert = findBoundaryEdgeInsertion(clickPt, prev, 1.0, 0.5);
                if (edgeInsert) {
                    const next = [...prev];
                    next.splice(edgeInsert.edgeIndex + 1, 0, edgeInsert.point);
                    return next;
                }
                return [...prev, clickPt];
            });
        });

        updateTileLayer('dark');

        return () => {
            map.remove();
            mapRef.current = null;
        };
    }, [setBoundary, setOrigin, beginUndoableEdit, rotation, setSelectedAreaIds, setSelectedCutLineId, setSelectedModuleId, areaLayouts, m1Params, m2Params]);

    useEffect(() => {
        if (boundary.length === 0) {
            setOrigin(null);
        }
    }, [boundary, setOrigin]);

    const updateTileLayer = (type) => {
        if (!mapRef.current) return;
        const map = mapRef.current;
        if (layersRef.current.tileLayer) {
            map.removeLayer(layersRef.current.tileLayer);
        }
        let newLayer;
        if (type === 'satellite') {
            newLayer = L.tileLayer(
                'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
                { maxZoom: 19 },
            );
        } else if (type === 'osm') {
            newLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{y}/{x}.png', {
                maxZoom: 19,
            });
        } else {
            newLayer = L.tileLayer(
                'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{y}/{x}{r}.png',
                { maxZoom: 20 },
            );
        }
        newLayer.addTo(map);
        layersRef.current.tileLayer = newLayer;
        setTileType(type);
    };

    const clearLayers = () => {
        const layers = layersRef.current;
        const map = mapRef.current;
        if (!map) return;
        layers.markers.forEach((m) => map.removeLayer(m));
        layers.markers = [];
        if (layers.boundaryPolyline) {
            map.removeLayer(layers.boundaryPolyline);
            layers.boundaryPolyline = null;
        }
        if (layers.boundaryPolygon) {
            map.removeLayer(layers.boundaryPolygon);
            layers.boundaryPolygon = null;
        }
        layers.usablePolygons.forEach((p) => map.removeLayer(p));
        layers.usablePolygons = [];
        layers.subAreaPolygons.forEach((p) => map.removeLayer(p));
        layers.subAreaPolygons = [];
        layers.cutLines.forEach((l) => map.removeLayer(l));
        layers.cutLines = [];
        layers.hatchOverlay.forEach((l) => map.removeLayer(l));
        layers.hatchOverlay = [];
        layers.modules.forEach((m) => map.removeLayer(m));
        layers.modules = [];
        layers.edgeLabels.forEach((l) => map.removeLayer(l));
        layers.edgeLabels = [];
    };

    const handleM2Delete = (idx) => {
        if (!results?.module2) return;
        beginUndoableEdit();
        const updated = results.module2.filter((_, i) => i !== idx);
        setContextMenu(null);
        onM2Commit(updated);
    };

    const handleM2RotateStart = (idx) => {
        if (!results?.module2?.[idx]) return;
        beginUndoableEdit();
        const mod = results.module2[idx];
        pointerStillSinceRef.current = Date.now();
        lastPointerRef.current = null;
        setRotation({
            m2Idx: idx,
            angle: mod.angle,
            latchedAngle: null,
            manualInput: false,
            manualValue: String(Math.round(mod.angle)),
        });
        setDraftM2List([...results.module2]);
        setContextMenu(null);
    };

    const commitRotation = useCallback(
        (angleDeg) => {
            if (!rotation || !results?.module2) return;
            const source = draftM2List ?? results.module2;
            const mod = source[rotation.m2Idx];
            const finalAngle = normalizeAngle360(Math.round(angleDeg));
            const updated = [...source];
            updated[rotation.m2Idx] = { ...mod, angle: finalAngle };
            if (!moduleFitsUsableArea(mod.x, mod.y, m2Len, m2Wid, finalAngle, usablePolygons)) {
                setRotation(null);
                setDraftM2List(null);
                return;
            }
            setRotation(null);
            setDraftM2List(null);
            onM2Commit(updated);
        },
        [rotation, results, draftM2List, m2Len, m2Wid, usablePolygons, onM2Commit],
    );

    const cancelRotation = () => {
        setRotation(null);
        setDraftM2List(null);
    };

    useEffect(() => {
        if (!rotation || !mapRef.current) return undefined;
        const map = mapRef.current;

        const onMouseMove = (e) => {
            if (rotation.manualInput) return;
            const meters = latLngToMeters(e.latlng.lat, e.latlng.lng, origin);
            const mod = (draftM2List ?? results?.module2)?.[rotation.m2Idx];
            if (!mod) return;

            const pointerKey = `${meters[0].toFixed(2)},${meters[1].toFixed(2)}`;
            if (lastPointerRef.current !== pointerKey) {
                lastPointerRef.current = pointerKey;
                pointerStillSinceRef.current = Date.now();
            }
            const stillMs = Date.now() - pointerStillSinceRef.current;
            const rawAngle = pointerToModuleAngle(meters[0], meters[1], mod.x, mod.y);
            const { angle, latched } = snapAngleWithStillThreshold(
                rawAngle,
                rotation.latchedAngle,
                stillMs,
            );
            const normalized = normalizeAngle360(Math.round(angle));
            if (moduleFitsUsableArea(mod.x, mod.y, m2Len, m2Wid, normalized, usablePolygons)) {
                const updated = [...(draftM2List ?? results.module2)];
                updated[rotation.m2Idx] = { ...mod, angle: normalized };
                setDraftM2List(updated);
                setRotation((prev) => ({
                    ...prev,
                    angle: normalized,
                    latchedAngle: latched,
                    manualValue: String(normalized),
                }));
            }
        };

        const onClick = () => {
            if (!rotation.manualInput) {
                const mod = (draftM2List ?? results?.module2)?.[rotation.m2Idx];
                if (mod) commitRotation(mod.angle);
            }
        };

        map.on('mousemove', onMouseMove);
        map.on('click', onClick);
        return () => {
            map.off('mousemove', onMouseMove);
            map.off('click', onClick);
        };
    }, [rotation, draftM2List, results, origin, m2Len, m2Wid, usablePolygons, commitRotation]);

    useEffect(() => {
        if (!rotation) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                cancelRotation();
            } else if (e.key === 'Tab') {
                e.preventDefault();
                setRotation((prev) => ({ ...prev, manualInput: true }));
                setTimeout(() => rotationInputRef.current?.focus(), 0);
            } else if (e.key === 'Enter' && rotation.manualInput) {
                e.preventDefault();
                const val = parseFloat(rotation.manualValue);
                if (!Number.isNaN(val)) commitRotation(val);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [rotation, commitRotation]);

    useEffect(() => {
        if (!mapRef.current || !origin) {
            clearLayers();
            return;
        }

        const map = mapRef.current;
        const layers = layersRef.current;
        clearLayers();

        const latlngs = boundary.map((pt) => {
            const coord = metersToLatLng(pt[0], pt[1], origin);
            return [coord.lat, coord.lng];
        });

        if (boundary.length >= 2 && boundary.length < 3) {
            layers.boundaryPolyline = L.polyline(latlngs, {
                color: '#2dd4bf',
                weight: 3,
            }).addTo(map);
        }

        if (boundary.length >= 3) {
            layers.boundaryPolygon = L.polygon(latlngs, {
                color: '#2dd4bf',
                weight: 3,
                fillColor: '#2dd4bf',
                fillOpacity: 0.05,
            }).addTo(map);
        }

        if (hasSplitSubAreas) {
            usablePolygons.forEach((poly) => {
                const uLatLngs = poly.map((pt) => {
                    const c = metersToLatLng(pt[0], pt[1], origin);
                    return [c.lat, c.lng];
                });
                const usablePoly = L.polygon(uLatLngs, {
                    color: '#fbbf24',
                    weight: 1.5,
                    fillColor: '#fbbf24',
                    fillOpacity: 0.04,
                    dashArray: '6 4',
                }).addTo(map);
                layers.usablePolygons.push(usablePoly);
            });
        }

        (subAreas || []).forEach((area, idx) => {
            if (!area.boundary) return;
            const latlngs = area.boundary.map((pt) => {
                const c = metersToLatLng(pt[0], pt[1], origin);
                return [c.lat, c.lng];
            });
            const isSelected = selectedAreaIds?.includes(area.id);
            const isCorridor = area.is_corridor;
            const isEnclosure = area.is_enclosure;
            const colorIdx = idx % SUB_AREA_STROKES.length;
            const poly = L.polygon(latlngs, {
                color: isCorridor
                    ? (isSelected ? '#fdba74' : '#fb923c')
                    : isEnclosure
                      ? (isSelected ? '#7dd3fc' : '#38bdf8')
                      : (isSelected ? '#c4b5fd' : SUB_AREA_STROKES[colorIdx]),
                weight: isSelected ? 2.5 : 1.5,
                fillColor: isCorridor
                    ? '#fb923c'
                    : isEnclosure
                      ? '#38bdf8'
                      : (isSelected ? '#a78bfa' : SUB_AREA_STROKES[colorIdx]),
                fillOpacity: isCorridor
                    ? (isSelected ? 0.28 : 0.16)
                    : isEnclosure
                      ? (isSelected ? 0.28 : 0.16)
                      : (isSelected ? 0.22 : 0.1),
            }).addTo(map);
            if (manualMode && toolMode === 'select') {
                poly.on('click', (ev) => {
                    L.DomEvent.stopPropagation(ev);
                    setSelectedCutLineId(null);
                    setSelectedModuleId(null);
                    setSelectedAreaIds((prev) =>
                        updateAreaSelection(prev, area.id, ev.originalEvent?.shiftKey),
                    );
                });
            }
            layers.subAreaPolygons.push(poly);

            if (area.boundary?.length >= 3) {
                const [cx, cy] = getPolygonCentroid(area.boundary);
                const c = metersToLatLng(cx, cy, origin);
                const label = formatAreaLabel(idx);
                const icon = L.divIcon({
                    className: 'sp-area-label-marker',
                    html: `<span class="sp-area-label">${label}</span>`,
                    iconSize: [32, 22],
                    iconAnchor: [16, 11],
                });
                const marker = L.marker([c.lat, c.lng], { icon, interactive: false }).addTo(map);
                layers.subAreaPolygons.push(marker);
            }
        });

        const activeHatchAreaId =
            selectedAreaIds?.length === 1 ? selectedAreaIds[0] : null;
        const activeHatch = activeHatchAreaId
            ? areaHatchConfigs?.[activeHatchAreaId]
            : null;

        if (activeHatch?.origin) {
            const c = metersToLatLng(activeHatch.origin[0], activeHatch.origin[1], origin);
            const marker = L.circleMarker([c.lat, c.lng], {
                radius: 6,
                color: '#0f172a',
                weight: 1.5,
                fillColor: '#a78bfa',
                fillOpacity: 1,
            }).addTo(map);
            layers.hatchOverlay.push(marker);
        }

        if (activeHatch?.angleRef) {
            const c = metersToLatLng(activeHatch.angleRef[0], activeHatch.angleRef[1], origin);
            const marker = L.circleMarker([c.lat, c.lng], {
                radius: 5,
                color: '#0f172a',
                weight: 1,
                fillColor: '#fbbf24',
                fillOpacity: 1,
            }).addTo(map);
            layers.hatchOverlay.push(marker);

            if (activeHatch.angle != null || hatchAnglePreview != null) {
                const displayAngle =
                    hatchAngleStep === 'direction' && hatchAnglePreview != null
                        ? hatchAnglePreview
                        : activeHatch.angle;
                const len = 0.0003;
                const rad = (displayAngle * Math.PI) / 180;
                const end = metersToLatLng(
                    activeHatch.angleRef[0] + len * Math.cos(rad) * 1000,
                    activeHatch.angleRef[1] + len * Math.sin(rad) * 1000,
                    origin,
                );
                const line = L.polyline(
                    [
                        [c.lat, c.lng],
                        [end.lat, end.lng],
                    ],
                    { color: '#a78bfa', weight: 2 },
                ).addTo(map);
                layers.hatchOverlay.push(line);
            }
        }

        (cutLines || []).forEach((line) => {
            if (!line.p1 || !line.p2) return;
            const isSelected = line.id === selectedCutLineId;
            const c1 = metersToLatLng(line.p1[0], line.p1[1], origin);
            const c2 = metersToLatLng(line.p2[0], line.p2[1], origin);
            const pl = L.polyline(
                [
                    [c1.lat, c1.lng],
                    [c2.lat, c2.lng],
                ],
                {
                    color: isSelected ? '#fca5a5' : '#f87171',
                    weight: isSelected ? 3 : 2,
                    dashArray: '8 4',
                },
            ).addTo(map);
            if (manualMode && toolMode === 'select') {
                pl.on('click', (ev) => {
                    L.DomEvent.stopPropagation(ev);
                    setSelectedCutLineId(line.id);
                    setSelectedModuleId(null);
                    setSelectedAreaIds([]);
                });
            }
            layers.cutLines.push(pl);

            if (isSelected) {
                [line.p1, line.p2].forEach((pt) => {
                    const c = metersToLatLng(pt[0], pt[1], origin);
                    const node = L.circleMarker([c.lat, c.lng], {
                        radius: 7,
                        color: '#0f172a',
                        weight: 1.5,
                        fillColor: '#fca5a5',
                        fillOpacity: 1,
                    }).addTo(map);
                    layers.cutLines.push(node);
                });
            }
        });

        (corridorWallLines || []).forEach((line) => {
            if (!line.p1 || !line.p2) return;
            const c1 = metersToLatLng(line.p1[0], line.p1[1], origin);
            const c2 = metersToLatLng(line.p2[0], line.p2[1], origin);
            const pl = L.polyline(
                [
                    [c1.lat, c1.lng],
                    [c2.lat, c2.lng],
                ],
                {
                    color: '#fb923c',
                    weight: 2,
                    dashArray: '6 4',
                    opacity: 0.9,
                },
            ).addTo(map);
            layers.cutLines.push(pl);
        });

        if (splitDraftPoints?.length > 0 && origin) {
            const previewPoints =
                splitCursor && splitDraftPoints.length >= 1
                    ? [...splitDraftPoints, splitCursor]
                    : splitDraftPoints;
            const centerLatLngs = previewPoints.map((pt) => {
                const c = metersToLatLng(pt[0], pt[1], origin);
                return [c.lat, c.lng];
            });
            const centerLine = L.polyline(centerLatLngs, {
                color: '#f87171',
                weight: 2,
                dashArray: '4 4',
            }).addTo(map);
            layers.cutLines.push(centerLine);

            splitDraftPoints.forEach((pt) => {
                const c = metersToLatLng(pt[0], pt[1], origin);
                const node = L.circleMarker([c.lat, c.lng], {
                    radius: 5,
                    color: '#0f172a',
                    weight: 1,
                    fillColor: '#f87171',
                    fillOpacity: 1,
                }).addTo(map);
                layers.cutLines.push(node);
            });
        }

        if (corridorDraft?.length > 0 && origin) {
            const previewCenterline =
                corridorCursor && corridorDraft.length >= 1
                    ? [...corridorDraft, corridorCursor]
                    : corridorDraft;
            if (previewCenterline.length >= 2) {
                const previewLines = computeCorridorDisplayLines(
                    previewCenterline,
                    corridorWidth,
                    corridorBorderPolygons,
                );
                previewLines.forEach(([p1, p2]) => {
                    const c1 = metersToLatLng(p1[0], p1[1], origin);
                    const c2 = metersToLatLng(p2[0], p2[1], origin);
                    const pl = L.polyline(
                        [
                            [c1.lat, c1.lng],
                            [c2.lat, c2.lng],
                        ],
                        {
                            color: '#fb923c',
                            weight: 2,
                            dashArray: '6 4',
                            opacity: 0.85,
                        },
                    ).addTo(map);
                    layers.cutLines.push(pl);
                });
            }

            const centerLatLngs = previewCenterline.map((pt) => {
                const c = metersToLatLng(pt[0], pt[1], origin);
                return [c.lat, c.lng];
            });
            const centerLine = L.polyline(centerLatLngs, {
                color: '#fb923c',
                weight: 2,
                dashArray: '4 4',
            }).addTo(map);
            layers.cutLines.push(centerLine);

            corridorDraft.forEach((pt) => {
                const c = metersToLatLng(pt[0], pt[1], origin);
                const node = L.circleMarker([c.lat, c.lng], {
                    radius: 5,
                    color: '#0f172a',
                    weight: 1,
                    fillColor: '#fb923c',
                    fillOpacity: 1,
                }).addTo(map);
                layers.cutLines.push(node);
            });
        }

        if (rotation && results) {
            getUsableEdgeLabels(results).forEach(({ mid, angleDeg }) => {
                const c = metersToLatLng(mid[0], mid[1], origin);
                const icon = L.divIcon({
                    className: 'sp-edge-angle-label',
                    html: `<span>${angleDeg}°</span>`,
                    iconSize: [40, 16],
                    iconAnchor: [20, 8],
                });
                const marker = L.marker([c.lat, c.lng], { icon, interactive: false }).addTo(map);
                layers.edgeLabels.push(marker);
            });
        }

        boundary.forEach((pt, index) => {
            const coord = metersToLatLng(pt[0], pt[1], origin);
            const icon = L.divIcon({
                className: 'custom-vertex-marker',
                html: '<div style="width: 12px; height: 12px; background: #2dd4bf; border: 2px solid #0f172a; border-radius: 50%; cursor: pointer;"></div>',
                iconSize: [12, 12],
                iconAnchor: [6, 6],
            });
            const marker = L.marker([coord.lat, coord.lng], { icon, draggable: true }).addTo(map);

            marker.on('dragstart', () => beginUndoableEdit());

            marker.on('drag', (e) => {
                const newLatLng = e.target.getLatLng();
                const newMeters = latLngToMeters(newLatLng.lat, newLatLng.lng, origin);
                setBoundary((prev) => {
                    const copy = [...prev];
                    copy[index] = newMeters;
                    return copy;
                });
            });

            marker.on('contextmenu', (e) => {
                L.DomEvent.preventDefault(e);
                beginUndoableEdit();
                setBoundary((prev) => prev.filter((_, idx) => idx !== index));
            });

            layers.markers.push(marker);
        });

        if (results) {
            if (results.mode === 'campus' && origin && !results.groups?.length) {
                const addRing = (ring, style) => {
                    if (!ring || ring.length < 3) return;
                    const latlngs = ring.map((pt) => {
                        const ll = metersToLatLng(pt[0], pt[1], origin);
                        return [ll.lat, ll.lng];
                    });
                    const poly = L.polygon(latlngs, {
                        color: style.color,
                        weight: 1.5,
                        fillColor: style.color,
                        fillOpacity: style.fillOpacity,
                        interactive: false,
                    }).addTo(map);
                    layers.modules.push(poly);
                };
                const labelAt = (ring, text, color) => {
                    if (!ring || ring.length < 3) return;
                    const [x, y] = getPolygonCentroid(ring);
                    const ll = metersToLatLng(x, y, origin);
                    const icon = L.divIcon({
                        className: 'sp-campus-label',
                        html: `<span style="color:${color};font-size:11px;font-weight:700">${text}</span>`,
                        iconSize: [28, 14],
                        iconAnchor: [14, 7],
                    });
                    layers.modules.push(
                        L.marker([ll.lat, ll.lng], { icon, interactive: false }).addTo(map),
                    );
                };
                (results.corridors || []).forEach((corridor) => {
                    const horizontal = corridor.kind === 'ch';
                    addRing(corridor.boundary, horizontal
                        ? { color: '#2dd4bf', fillOpacity: 0.22 }
                        : { color: '#94a3b8', fillOpacity: 0.28 });
                    labelAt(corridor.boundary, horizontal ? 'CH' : 'c', horizontal ? '#2dd4bf' : '#94a3b8');
                });
                if (results.central?.boundary) {
                    addRing(results.central.boundary, { color: '#a78bfa', fillOpacity: 0.35 });
                    labelAt(results.central.boundary, 'DH', '#a78bfa');
                }
            }
            if (results.groups?.length) {
                const m1Len = m1Params.length || 12.192;
                const m1Wid = m1Params.width || 2.438;
                let m1Flat = 0;
                let m2Flat = 0;
                const bindModuleBodyDrag = (poly, marker, mod, length, width, onCommit, selectionId) => {
                    if (!(manualMode && toolMode === 'select')) return;
                    poly.on('mousedown', (e) => {
                        L.DomEvent.stop(e);
                        const start = latLngToMeters(e.latlng.lat, e.latlng.lng, origin);
                        const originMod = { x: mod.x, y: mod.y, angle: mod.angle };
                        let pending = null;
                        let moved = false;
                        map.dragging.disable();
                        const onMove = (ev) => {
                            const now = latLngToMeters(ev.latlng.lat, ev.latlng.lng, origin);
                            const x = originMod.x + (now[0] - start[0]);
                            const y = originMod.y + (now[1] - start[1]);
                            if (!moduleFitsUsableArea(x, y, length, width, originMod.angle, usablePolygons)) return;
                            if (!moved) {
                                beginUndoableEdit();
                                moved = true;
                            }
                            pending = { x, y };
                            poly.setLatLngs(getOrientedBoxLatLngs(
                                x, y, length, width, originMod.angle, origin,
                            ));
                            marker?.setLatLng(metersToLatLng(x, y, origin));
                        };
                        const onUp = () => {
                            map.dragging.enable();
                            map.off('mousemove', onMove);
                            map.off('mouseup', onUp);
                            if (pending) onCommit(pending.x, pending.y);
                            setSelectedModuleId(selectionId);
                            setSelectedCutLineId(null);
                            setSelectedAreaIds([]);
                        };
                        map.on('mousemove', onMove);
                        map.on('mouseup', onUp);
                    });
                };
                const clusterParts = [];
                results.groups.forEach((group, groupIndex) => {
                    const accepted = group.accepted;
                    const outline = group.outline || [];
                    const canDragGroup = manualMode && toolMode === 'select';
                    const groupSelected = selectedModuleId === formatGroupSelectionId(groupIndex);
                    const moveables = [];
                    const dragRings = [];
                    const dragLabels = [];
                    const campusMode = results.mode === 'campus';
                    let beginGroupDrag = null;
                    let outlinePoly = null;
                    if (outline.length >= 3) {
                        const latlngs = outline.map((pt) => {
                            const ll = metersToLatLng(pt[0], pt[1], origin);
                            return [ll.lat, ll.lng];
                        });
                        outlinePoly = L.polygon(latlngs, {
                            color: groupSelected ? '#e2e8f0' : (accepted ? '#94a3b8' : 'rgba(148, 163, 184, 0.8)'),
                            weight: groupSelected ? 2.5 : (accepted ? 2 : 1),
                            dashArray: accepted ? null : '6 4',
                            fillOpacity: 0,
                            interactive: canDragGroup,
                        }).addTo(map);
                        if (canDragGroup) {
                            beginGroupDrag = (e) => {
                                L.DomEvent.stop(e);
                                const start = latLngToMeters(e.latlng.lat, e.latlng.lng, origin);
                                const snapshot = JSON.parse(JSON.stringify(group));
                                let pending = null;
                                let moved = false;
                                map.dragging.disable();
                                const applyShift = (dx, dy) => {
                                    if (outlinePoly && snapshot.outline) {
                                        outlinePoly.setLatLngs(snapshot.outline.map((pt) => {
                                            const ll = metersToLatLng(pt[0] + dx, pt[1] + dy, origin);
                                            return [ll.lat, ll.lng];
                                        }));
                                    }
                                    moveables.forEach((item) => {
                                        item.poly.setLatLngs(getOrientedBoxLatLngs(
                                            item.x + dx,
                                            item.y + dy,
                                            item.length,
                                            item.width,
                                            item.angle,
                                            origin,
                                        ));
                                        item.marker?.setLatLng(
                                            metersToLatLng(item.x + dx, item.y + dy, origin),
                                        );
                                    });
                                    dragRings.forEach((ring) => {
                                        ring.poly.setLatLngs(ring.points.map((pt) => {
                                            const ll = metersToLatLng(pt[0] + dx, pt[1] + dy, origin);
                                            return [ll.lat, ll.lng];
                                        }));
                                    });
                                    dragLabels.forEach((item) => {
                                        item.marker.setLatLng(
                                            metersToLatLng(item.x + dx, item.y + dy, origin),
                                        );
                                    });
                                };
                                const onMove = (ev) => {
                                    const now = latLngToMeters(ev.latlng.lat, ev.latlng.lng, origin);
                                    const dx = now[0] - start[0];
                                    const dy = now[1] - start[1];
                                    const m1Ok = (snapshot.module1 || []).every((mod) => moduleFitsUsableArea(
                                        mod.x + dx, mod.y + dy, m1Len, m1Wid, mod.angle, usablePolygons,
                                    ));
                                    const fits = campusMode
                                        ? campusRigidFits(snapshot, dx, dy, m1Len, m1Wid, usablePolygons)
                                        : m1Ok && (snapshot.module2 || []).every((mod) => moduleFitsUsableArea(
                                            mod.x + dx, mod.y + dy, m2Len, m2Wid, mod.angle, usablePolygons,
                                        ));
                                    if (!fits) return;
                                    if (!moved) {
                                        beginUndoableEdit();
                                        moved = true;
                                    }
                                    pending = { dx, dy };
                                    applyShift(dx, dy);
                                };
                                const onUp = () => {
                                    map.dragging.enable();
                                    map.off('mousemove', onMove);
                                    map.off('mouseup', onUp);
                                    if (pending) {
                                        onTranslateGroupRef.current?.(
                                            groupIndex,
                                            pending.dx,
                                            pending.dy,
                                            snapshot,
                                        );
                                        if (campusMode) {
                                            onRefitCampusRef.current?.(
                                                groupIndex,
                                                pending.dx,
                                                pending.dy,
                                                snapshot,
                                            );
                                        }
                                    }
                                    setSelectedModuleId(formatGroupSelectionId(groupIndex));
                                    setSelectedCutLineId(null);
                                    setSelectedAreaIds([]);
                                };
                                map.on('mousemove', onMove);
                                map.on('mouseup', onUp);
                            };
                            outlinePoly.on('mousedown', beginGroupDrag);
                        }
                        layers.modules.push(outlinePoly);
                    }
                    if (campusMode) {
                        const addCampusRing = (ring, style) => {
                            if (!ring || ring.length < 3) return;
                            const latlngs = ring.map((pt) => {
                                const ll = metersToLatLng(pt[0], pt[1], origin);
                                return [ll.lat, ll.lng];
                            });
                            const poly = L.polygon(latlngs, {
                                color: style.color,
                                weight: 1.5,
                                fillColor: style.color,
                                fillOpacity: style.fillOpacity,
                                interactive: Boolean(beginGroupDrag),
                            }).addTo(map);
                            if (beginGroupDrag) poly.on('mousedown', beginGroupDrag);
                            dragRings.push({ poly, points: ring });
                            layers.modules.push(poly);
                        };
                        const labelRing = (ring, text, color) => {
                            if (!ring || ring.length < 3) return;
                            const [x, y] = getPolygonCentroid(ring);
                            const ll = metersToLatLng(x, y, origin);
                            const icon = L.divIcon({
                                className: 'sp-campus-label',
                                html: `<span style="color:${color};font-size:11px;font-weight:700">${text}</span>`,
                                iconSize: [28, 14],
                                iconAnchor: [14, 7],
                            });
                            const marker = L.marker([ll.lat, ll.lng], { icon, interactive: false }).addTo(map);
                            dragLabels.push({ marker, x, y });
                            layers.modules.push(marker);
                        };
                        (group.corridors || []).forEach((corridor) => {
                            const horizontal = corridor.kind === 'ch';
                            addCampusRing(corridor.boundary, horizontal
                                ? { color: '#2dd4bf', fillOpacity: 0.22 }
                                : { color: '#94a3b8', fillOpacity: 0.28 });
                            labelRing(corridor.boundary, horizontal ? 'CH' : 'c', horizontal ? '#2dd4bf' : '#94a3b8');
                        });
                        if (group.central?.boundary) {
                            addCampusRing(group.central.boundary, { color: '#a78bfa', fillOpacity: 0.35 });
                            labelRing(group.central.boundary, 'DH', '#a78bfa');
                        }
                    }
                    (group.module1 || []).forEach((m, index) => {
                        const flatIndex = m1Flat + index;
                        const isSelected = selectedModuleId === formatResultM1ModuleId(flatIndex);
                        const rectPoints = getOrientedBoxLatLngs(m.x, m.y, m1Len, m1Wid, m.angle, origin);
                        const poly = L.polygon(rectPoints, {
                            color: isSelected ? '#7dd3fc' : (accepted ? '#34d399' : 'rgba(52, 211, 153, 0.5)'),
                            weight: isSelected ? 2.5 : 1,
                            fillColor: '#34d399',
                            fillOpacity: isSelected ? 0.55 : (accepted ? 0.35 : 0.16),
                        }).addTo(map);
                        layers.modules.push(poly);
                        const marker = campusMode ? null : attachSelectDragHandle(
                            map,
                            m.x,
                            m.y,
                            m1Len,
                            m1Wid,
                            m.angle,
                            '#34d399',
                            (x, y) => onUpdateResultM1Ref.current?.(flatIndex, { x, y }),
                            layers,
                            poly,
                        );
                        moveables.push({
                            poly,
                            marker,
                            x: m.x,
                            y: m.y,
                            length: m1Len,
                            width: m1Wid,
                            angle: m.angle,
                        });
                        if (campusMode && beginGroupDrag) {
                            poly.on('mousedown', beginGroupDrag);
                        } else {
                            bindModuleBodyDrag(
                                poly,
                                marker,
                                m,
                                m1Len,
                                m1Wid,
                                (x, y) => onUpdateResultM1Ref.current?.(flatIndex, { x, y }),
                                formatResultM1ModuleId(flatIndex),
                            );
                        }
                    });
                    (group.module2 || []).forEach((m, index) => {
                        const flatIndex = m2Flat + index;
                        const isSelected = selectedModuleId === formatResultM2ModuleId(flatIndex);
                        const rectPoints = getOrientedBoxLatLngs(m.x, m.y, m2Len, m2Wid, m.angle, origin);
                        const poly = L.polygon(rectPoints, {
                            color: isSelected ? '#f9a8d4' : (accepted ? '#ec4899' : 'rgba(236, 72, 153, 0.5)'),
                            weight: isSelected ? 2.5 : 1,
                            fillColor: '#ec4899',
                            fillOpacity: isSelected ? 0.55 : (accepted ? 0.35 : 0.16),
                        }).addTo(map);
                        layers.modules.push(poly);
                        const marker = campusMode ? null : attachSelectDragHandle(
                            map,
                            m.x,
                            m.y,
                            m2Len,
                            m2Wid,
                            m.angle,
                            '#ec4899',
                            (x, y) => onUpdateResultM2Ref.current?.(flatIndex, { x, y }),
                            layers,
                            poly,
                        );
                        moveables.push({
                            poly,
                            marker,
                            x: m.x,
                            y: m.y,
                            length: m2Len,
                            width: m2Wid,
                            angle: m.angle,
                        });
                        if (campusMode && beginGroupDrag) {
                            poly.on('mousedown', beginGroupDrag);
                        } else {
                            bindModuleBodyDrag(
                                poly,
                                marker,
                                m,
                                m2Len,
                                m2Wid,
                                (x, y) => onUpdateResultM2Ref.current?.(flatIndex, { x, y }),
                                formatResultM2ModuleId(flatIndex),
                            );
                        }
                    });
                    m1Flat += (group.module1 || []).length;
                    m2Flat += (group.module2 || []).length;
                    clusterParts.push({ outlinePoly, moveables });
                });
                const clusterCentroid = results.strategy_name === 'cluster_m1'
                    ? groupClusterCentroid(results.groups)
                    : null;
                if (clusterCentroid && manualMode && toolMode === 'select') {
                    const [cx, cy] = clusterCentroid;
                    const centerCoord = metersToLatLng(cx, cy, origin);
                    const dragIcon = L.divIcon({
                        className: 'custom-cluster-drag-handle',
                        html: '<div style="width: 16px; height: 16px; background: #f8fafc; border: 2px solid #0f172a; border-radius: 3px; cursor: move;"></div>',
                        iconSize: [16, 16],
                        iconAnchor: [8, 8],
                    });
                    const dragMarker = L.marker([centerCoord.lat, centerCoord.lng], {
                        icon: dragIcon,
                        draggable: true,
                        zIndexOffset: 800,
                    }).addTo(map);
                    let pending = null;
                    let moved = false;
                    const snapshots = JSON.parse(JSON.stringify(results.groups));
                    dragMarker.on('drag', (e) => {
                        const now = latLngToMeters(e.target.getLatLng().lat, e.target.getLatLng().lng, origin);
                        const dx = now[0] - cx;
                        const dy = now[1] - cy;
                        const fits = snapshots.every((group) => (
                            (group.module1 || []).every((mod) => moduleFitsUsableArea(
                                mod.x + dx, mod.y + dy, m1Len, m1Wid, mod.angle, usablePolygons,
                            ))
                            && (group.module2 || []).every((mod) => moduleFitsUsableArea(
                                mod.x + dx, mod.y + dy, m2Len, m2Wid, mod.angle, usablePolygons,
                            ))
                        ));
                        if (!fits) {
                            if (pending) {
                                e.target.setLatLng(metersToLatLng(cx + pending.dx, cy + pending.dy, origin));
                            } else {
                                e.target.setLatLng(centerCoord);
                            }
                            return;
                        }
                        if (!moved) {
                            beginUndoableEdit();
                            moved = true;
                        }
                        pending = { dx, dy };
                        clusterParts.forEach((part, index) => {
                            const group = snapshots[index];
                            if (part.outlinePoly && group.outline) {
                                part.outlinePoly.setLatLngs(group.outline.map((pt) => {
                                    const ll = metersToLatLng(pt[0] + dx, pt[1] + dy, origin);
                                    return [ll.lat, ll.lng];
                                }));
                            }
                            part.moveables.forEach((item) => {
                                item.poly.setLatLngs(getOrientedBoxLatLngs(
                                    item.x + dx, item.y + dy, item.length, item.width, item.angle, origin,
                                ));
                                item.marker?.setLatLng(metersToLatLng(item.x + dx, item.y + dy, origin));
                            });
                        });
                    });
                    dragMarker.on('dragend', () => {
                        if (pending) {
                            onTranslateClusterRef.current?.(pending.dx, pending.dy, snapshots);
                        }
                    });
                    layers.modules.push(dragMarker);
                }
            } else if (results.module1) {
                const m1Len = m1Params.length || 12.192;
                const m1Wid = m1Params.width || 2.438;
                results.module1.forEach((m, index) => {
                    const resultKey = formatResultM1ModuleId(index);
                    const isSelected = selectedModuleId === resultKey;
                    const rectPoints = getOrientedBoxLatLngs(m.x, m.y, m1Len, m1Wid, m.angle, origin);
                    const poly = L.polygon(rectPoints, {
                        color: isSelected ? '#7dd3fc' : '#38bdf8',
                        weight: isSelected ? 2.5 : 1,
                        fillColor: '#38bdf8',
                        fillOpacity: isSelected ? 0.5 : 0.3,
                    }).addTo(map);
                    layers.modules.push(poly);
                    attachSelectDragHandle(
                        map,
                        m.x,
                        m.y,
                        m1Len,
                        m1Wid,
                        m.angle,
                        '#38bdf8',
                        (x, y) => onUpdateResultM1Ref.current?.(index, { x, y }),
                        layers,
                        poly,
                    );
                });
            }

            if (manualModules.length > 0) {
                const m1Len = m1Params.length || 12.192;
                const m1Wid = m1Params.width || 2.438;
                manualModules.forEach((m) => {
                    const hatchKey = formatHatchModuleId(m.areaId, m.hatchIndex);
                    const isSelected = selectedModuleId === hatchKey;
                    const rectPoints = getOrientedBoxLatLngs(m.x, m.y, m1Len, m1Wid, m.angle, origin);
                    const poly = L.polygon(rectPoints, {
                        color: isSelected ? '#6ee7b7' : '#34d399',
                        weight: isSelected ? 2.5 : 1.5,
                        fillColor: '#34d399',
                        fillOpacity: isSelected ? 0.5 : 0.35,
                    }).addTo(map);
                    layers.modules.push(poly);
                    attachSelectDragHandle(
                        map,
                        m.x,
                        m.y,
                        m1Len,
                        m1Wid,
                        m.angle,
                        '#34d399',
                        (x, y) =>
                            onUpdateHatchModuleRef.current?.(m.areaId, m.hatchIndex, { x, y }),
                        layers,
                        poly,
                    );
                });
            }

            (placedModules || []).forEach((m) => {
                const dims = getPlacedModuleDimensions(m.type, m1Params, m2Params, m3Params, m4Params);
                const isSelected = selectedModuleId === m.id;
                const colors = { M1: '#34d399', M2: '#ec4899', M3: '#fbbf24', M4: '#60a5fa' };
                const color = colors[m.type] || '#34d399';
                const rectPoints = getOrientedBoxLatLngs(
                    m.x,
                    m.y,
                    dims.length,
                    dims.width,
                    m.angle,
                    origin,
                );
                const poly = L.polygon(rectPoints, {
                    color: isSelected ? '#f8fafc' : color,
                    weight: isSelected ? 2.5 : 1.5,
                    fillColor: color,
                    fillOpacity: isSelected ? 0.55 : 0.4,
                }).addTo(map);
                layers.modules.push(poly);
                attachSelectDragHandle(
                    map,
                    m.x,
                    m.y,
                    dims.length,
                    dims.width,
                    m.angle,
                    color,
                    (x, y) => onUpdatePlacedModuleRef.current?.(m.id, { x, y }),
                    layers,
                    poly,
                );
            });

            (module3Positions || []).forEach((m) => {
                const rectPoints = getOrientedBoxLatLngs(m.x, m.y, m3Len, m3Wid, m.angle ?? 0, origin);
                const poly = L.polygon(rectPoints, {
                    color: '#fbbf24',
                    weight: 2,
                    fillColor: '#fbbf24',
                    fillOpacity: 0.45,
                }).addTo(map);
                layers.modules.push(poly);
            });

            (module4Positions || []).forEach((m) => {
                const rectPoints = getOrientedBoxLatLngs(m.x, m.y, m4Len, m4Wid, m.angle ?? 0, origin);
                const poly = L.polygon(rectPoints, {
                    color: '#60a5fa',
                    weight: 2,
                    fillColor: '#60a5fa',
                    fillOpacity: 0.45,
                }).addTo(map);
                layers.modules.push(poly);
            });

            const groupedM2Count = (results.groups || []).reduce(
                (count, group) => count + (group.module2 || []).length,
                0,
            );
            if (displayM2 && groupedM2Count !== displayM2.length) {
                displayM2.forEach((m, idx) => {
                    const rectPoints = getOrientedBoxLatLngs(m.x, m.y, m2Len, m2Wid, m.angle, origin);
                    const poly = L.polygon(rectPoints, {
                        color: rotation?.m2Idx === idx ? '#f43f5e' : '#ec4899',
                        weight: 2,
                        fillColor: '#ec4899',
                        fillOpacity: rotation?.m2Idx === idx ? 0.55 : 0.4,
                    }).addTo(map);

                    poly.on('contextmenu', (e) => {
                        L.DomEvent.preventDefault(e);
                        L.DomEvent.stopPropagation(e);
                        setContextMenu({
                            m2Idx: idx,
                            screenX: e.originalEvent.clientX,
                            screenY: e.originalEvent.clientY,
                        });
                    });

                    const centerCoord = metersToLatLng(m.x, m.y, origin);
                    const dragIcon = L.divIcon({
                        className: 'custom-m2-drag-handle',
                        html: '<div style="width: 10px; height: 10px; background: #ec4899; border: 1.5px solid white; border-radius: 50%; cursor: move;"></div>',
                        iconSize: [10, 10],
                        iconAnchor: [5, 5],
                    });

                    const dragMarker = L.marker([centerCoord.lat, centerCoord.lng], {
                        icon: dragIcon,
                        draggable: !rotation,
                    }).addTo(map);

                    let localDraft = null;

                    dragMarker.on('dragstart', () => {
                        beginUndoableEdit();
                        localDraft = [...(draftM2List ?? results.module2)];
                    });

                    dragMarker.on('drag', (e) => {
                        const newLatLng = e.target.getLatLng();
                        const newMeters = latLngToMeters(newLatLng.lat, newLatLng.lng, origin);
                        const source = localDraft ?? displayM2;
                        const mod = source[idx];
                        const candidate = { ...mod, x: newMeters[0], y: newMeters[1] };
                        if (
                            moduleFitsUsableArea(
                                candidate.x,
                                candidate.y,
                                m2Len,
                                m2Wid,
                                mod.angle,
                                usablePolygons,
                            )
                        ) {
                            const updated = [...source];
                            updated[idx] = candidate;
                            localDraft = updated;
                            setDraftM2List(updated);
                        } else {
                            e.target.setLatLng(centerCoord);
                        }
                    });

                    dragMarker.on('dragend', () => {
                        if (localDraft) {
                            onM2Commit(localDraft);
                            setDraftM2List(null);
                            localDraft = null;
                        }
                    });

                    layers.modules.push(poly);
                    layers.modules.push(dragMarker);
                });
            }
        }
    }, [
        boundary,
        origin,
        results,
        displayM2,
        draftM2List,
        m1Params,
        m2Len,
        m2Wid,
        rotation,
        usablePolygons,
        hasSplitSubAreas,
        beginUndoableEdit,
        onM2Commit,
        getOrientedBoxLatLngs,
        attachSelectDragHandle,
        onUpdatePlacedModule,
        onUpdateHatchModule,
        onUpdateResultM1,
        subAreas,
        selectedAreaIds,
        selectedCutLineId,
        selectedModuleId,
        cutLines,
        corridorWallLines,
        hatchAngleStep,
        hatchAnglePreview,
        areaHatchConfigs,
        manualModules,
        placedModules,
        manualMode,
        toolMode,
        corridorDraft,
        corridorCursor,
        splitDraftPoints,
        splitCursor,
        corridorWidth,
        corridorBorderPolygons,
        setSelectedAreaIds,
    ]);

    return (
        <div className="sp-viewport-root">
            <div className="sp-viewport-mode-toggle">
                <button
                    type="button"
                    className={`sp-manual-toggle${manualMode ? ' on' : ''}`}
                    onClick={() => onManualModeChange(!manualMode)}
                >
                    <PenTool size={14} />
                    Manual Mode {manualMode ? 'ON' : 'OFF'}
                </button>
                {manualMode && (
                    <div className="sp-manual-tool-hint">
                        {toolMode === 'split' &&
                            'Chain snapped points · finish on border or close on first point · Esc cancels'}
                        {toolMode === 'corridor' &&
                            'Click centerline points (snapped) · Enter/double-click finishes · Esc cancels'}
                        {toolMode === 'select' &&
                            'Select areas, cut lines, or modules · Drag modules to reposition'}
                        {toolMode === 'hatchOrigin' &&
                            (hatchAngleStep === 'reference'
                                ? 'Click angle reference point'
                                : hatchAngleStep === 'direction'
                                  ? 'Adjust hatch angle · Tab keeps current angle'
                                  : 'Select one area, then click hatch origin')}
                    </div>
                )}
            </div>

            <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

            {contextMenu && (
                <div
                    className="sp-context-menu"
                    style={{ left: contextMenu.screenX, top: contextMenu.screenY }}
                >
                    <button type="button" onClick={() => handleM2RotateStart(contextMenu.m2Idx)}>
                        Rotate
                    </button>
                    <button
                        type="button"
                        className="sp-context-menu-danger"
                        onClick={() => handleM2Delete(contextMenu.m2Idx)}
                    >
                        Delete
                    </button>
                </div>
            )}

            {manualMode && toolMode === 'hatchOrigin' && hatchAngleStep === 'direction' && (
                <div className="sp-rotation-hud">
                    <span className="sp-rotation-angle">
                        {Math.round(hatchAnglePreview ?? activeHatch?.angle ?? 0)}°
                    </span>
                    <span className="sp-rotation-hint">
                        {activeHatch?.angle != null
                            ? 'Tab: keep angle · Click: confirm new angle'
                            : 'Click to set hatch angle'}
                    </span>
                </div>
            )}

            {rotation && (
                <div className="sp-rotation-hud">
                    {rotation.manualInput ? (
                        <input
                            ref={rotationInputRef}
                            type="number"
                            className="sp-rotation-input"
                            value={rotation.manualValue}
                            onChange={(e) =>
                                setRotation((prev) => ({ ...prev, manualValue: e.target.value }))
                            }
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const val = parseFloat(rotation.manualValue);
                                    if (!Number.isNaN(val)) commitRotation(val);
                                }
                            }}
                        />
                    ) : (
                        <span className="sp-rotation-angle">{Math.round(rotation.angle)}°</span>
                    )}
                    <span className="sp-rotation-hint">Tab: type angle · Click: confirm · Esc: cancel</span>
                </div>
            )}

            <div className="sp-map-selector">
                <button
                    type="button"
                    onClick={() => updateTileLayer('dark')}
                    className={`sp-map-btn ${tileType === 'dark' ? 'active' : ''}`}
                >
                    Dark Map
                </button>
                <button
                    type="button"
                    onClick={() => updateTileLayer('satellite')}
                    className={`sp-map-btn ${tileType === 'satellite' ? 'active' : ''}`}
                >
                    Satellite
                </button>
                <button
                    type="button"
                    onClick={() => updateTileLayer('osm')}
                    className={`sp-map-btn ${tileType === 'osm' ? 'active' : ''}`}
                >
                    Street
                </button>
            </div>

            {boundary.length === 0 && (
                <div className="sp-helper-overlay">
                    <div className="sp-helper-card">
                        <MapPin
                            className="sp-bounce"
                            style={{ margin: '0 auto 0.75rem', color: '#2dd4bf' }}
                            size={32}
                        />
                        <h3 style={{ color: '#e2e8f0', fontWeight: '700', marginBottom: '0.25rem' }}>
                            Define Site Boundary
                        </h3>
                        <p style={{ color: '#94a3b8', fontSize: '0.75rem', lineHeight: '1.5' }}>
                            Click points on the map to draw your site boundary. Right-click M2 modules
                            after optimization to delete or rotate. Ctrl+Z undoes edits.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
